import React from 'react';
import { CheckCircle, XCircle, Upload, AlertCircle } from 'lucide-react';
import { UploadProgress } from '../../services/FileService';

interface FileUploadProgressProps {
  uploads: UploadProgress[];
  onCancel?: (fileName: string) => void;
  className?: string;
}

export function FileUploadProgress({ uploads, onCancel, className = '' }: FileUploadProgressProps) {
  if (uploads.length === 0) return null;

  const totalProgress = uploads.reduce((sum, upload) => sum + upload.percentage, 0) / uploads.length;
  const completedUploads = uploads.filter(upload => upload.isComplete).length;
  const failedUploads = uploads.filter(upload => upload.error).length;

  return (
    <div className={`bg-white border border-gray-200 rounded-lg shadow-sm p-4 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-medium text-gray-900">
          Uploading Files ({completedUploads}/{uploads.length})
        </h3>
        <div className="text-xs text-gray-500">
          {Math.round(totalProgress)}% complete
        </div>
      </div>

      {/* Overall Progress */}
      <div className="mb-4">
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
            style={{ width: `${totalProgress}%` }}
          />
        </div>
      </div>

      {/* Individual File Progress */}
      <div className="space-y-2 max-h-40 overflow-y-auto">
        {uploads.map((upload, index) => (
          <FileUploadItem
            key={index}
            upload={upload}
            onCancel={onCancel}
          />
        ))}
      </div>

      {/* Summary */}
      {failedUploads > 0 && (
        <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-4 h-4" />
            {failedUploads} file(s) failed to upload
          </div>
        </div>
      )}
    </div>
  );
}

interface FileUploadItemProps {
  upload: UploadProgress;
  onCancel?: (fileName: string) => void;
}

function FileUploadItem({ upload, onCancel }: FileUploadItemProps) {
  const getStatusIcon = () => {
    if (upload.error) {
      return <XCircle className="w-4 h-4 text-red-500" />;
    }
    if (upload.isComplete) {
      return <CheckCircle className="w-4 h-4 text-green-500" />;
    }
    return <Upload className="w-4 h-4 text-blue-500 animate-pulse" />;
  };

  const getStatusText = () => {
    if (upload.error) return 'Failed';
    if (upload.isComplete) return 'Complete';
    return 'Uploading...';
  };

  const getStatusColor = () => {
    if (upload.error) return 'text-red-600';
    if (upload.isComplete) return 'text-green-600';
    return 'text-blue-600';
  };

  return (
    <div className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded">
      {/* Status Icon */}
      <div className="flex-shrink-0">
        {getStatusIcon()}
      </div>

      {/* File Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-1">
          <p className="text-sm font-medium text-gray-900 truncate">
            {upload.fileName}
          </p>
          <span className={`text-xs font-medium ${getStatusColor()}`}>
            {getStatusText()}
          </span>
        </div>

        {/* Progress Bar */}
        {!upload.isComplete && !upload.error && (
          <div className="w-full bg-gray-200 rounded-full h-1">
            <div
              className="bg-blue-600 h-1 rounded-full transition-all duration-300"
              style={{ width: `${upload.percentage}%` }}
            />
          </div>
        )}

        {/* File Size Info */}
        <div className="flex items-center justify-between mt-1">
          <span className="text-xs text-gray-500">
            {formatBytes(upload.bytesTransferred)} / {formatBytes(upload.totalBytes)}
          </span>
          {!upload.isComplete && !upload.error && (
            <span className="text-xs text-gray-500">
              {upload.percentage.toFixed(1)}%
            </span>
          )}
        </div>

        {/* Error Message */}
        {upload.error && (
          <p className="text-xs text-red-600 mt-1 truncate">
            {upload.error}
          </p>
        )}
      </div>

      {/* Cancel Button */}
      {!upload.isComplete && !upload.error && onCancel && (
        <button
          onClick={() => onCancel(upload.fileName)}
          className="flex-shrink-0 p-1 text-gray-400 hover:text-red-500 transition-colors"
          title="Cancel upload"
        >
          <XCircle className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

export default FileUploadProgress;